import { Loading } from '@/components/loading'
import { OFFSET } from '@/constants/Common'
import { ROUTE_EVENTS } from '@/constants/Routes'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { getClubRoles } from '@/pages/club/clubSlice'
import { clubSelector } from '@/pages/club/selectors'
import { userSelector } from '@/pages/user/selectors'
import { useGetBracketMatchStructure, useGetBracketStandings } from '@/services'
import { sortBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { VisualDiagram } from '../components/diagram/VisualDiagram'
import { getBracketDetail, getEventDetail } from '../eventSlice'
import { eventSelector } from '../selectors'

export const BracketDiagram = () => {
  const params = useParams()
  const navigate = useNavigate()

  const { eventId, bracketId } = params

  const dispatch = useAppDispatch()
  const { event, bracket } = useAppSelector(eventSelector)
  const { permissions } = useAppSelector(clubSelector)
  const { user } = useAppSelector(userSelector)

  const { data: resBracketMatchStructure } = useGetBracketMatchStructure(bracketId)
  const { data: resStandings } = useGetBracketStandings(bracketId, 3, OFFSET, 0)

  const [currentMatches, setCurrentMatches] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [eventFormat, setEventFormat] = useState('')

  useEffect(() => {
    if (bracketId) {
      dispatch(getBracketDetail(bracketId))
    }
    if (eventId) {
      dispatch(getEventDetail(eventId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bracketId, eventId])

  useEffect(() => {
    if (!permissions && user?.id && event) {
      dispatch(getClubRoles({ clubId: event?.clubId, userId: user?.id }))
    }
    if (!user) navigate(`${ROUTE_EVENTS}/${eventId}/bracket/${bracketId}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, event])

  useEffect(() => {
    setLoading(true)
    if (resBracketMatchStructure) {
      const { results } = resBracketMatchStructure
      setEventFormat(results?.[0]?.eventFormat)
      const totalRounds = Array.from(new Set(results.map((el: any) => el.round)))
      const allMatches = []
      for (let i = 1; i < totalRounds.length + 1; i += 1) {
        const ithRound = results.filter((el: any) => el.round === i)
        const ithRoundMatches = ithRound.map((el: any, index: number) => {
          const teams = sortBy(el?.teams, 'serial')
          let team1 = { serial: 1, winner: false }
          let team2 = { serial: 2, winner: false }
          if (teams.length === 2) {
            team1 = { ...teams?.[0], ...(el?.impactedDraw ? { winner: !teams?.[0]?.winner } : {}) }
            team2 = { ...teams?.[1], ...(el?.impactedDraw ? { winner: !teams?.[1]?.winner } : {}) }
          } else if (teams.length === 1) {
            if (teams?.[0]?.serial === 1) {
              team1 = { ...teams?.[0], ...(el?.impactedDraw ? { winner: !teams?.[0]?.winner } : {}) }
              team2 = { serial: 2, winner: false }
            } else {
              team1 = { serial: 1, winner: false }
              team2 = { ...teams?.[0], ...(el?.impactedDraw ? { winner: !teams?.[0]?.winner } : {}) }
            }
          }
          return {
            serial: index + 1,
            confirmed: el?.confirmed,
            impactedDraw: el?.impactedDraw,
            team1: { ...team1 },
            team2: { ...team2 },
            leagueMatchId: el?.leagueMatchId,
            bye: el?.isByeMatch,
            forfeited: el?.isForfeited,
            matchSerial: index + 1,
            tag: el?.tag,
            matchSlot: el?.matchSlot,
            is_team_1_tbd: el?.is_team_1_tbd,
            is_team_2_tbd: el?.is_team_2_tbd,
            teams: {
              team1: { ...team1 },
              team2: { ...team2 },
            },
            matchScoreAdded: el?.matchScoreAdded,
            noOfGames: el?.noOfGames,
          }
        })
        const obj = {
          serial: i,
          round: i,
          matches: ithRoundMatches,
          startDate: null,
          endDate: null,
        }
        allMatches.push(obj)
      }
      setCurrentMatches(allMatches)
      setLoading(false)
    }
  }, [resBracketMatchStructure])

  return (
    <>
      {bracket && event && permissions && !loading && currentMatches.length && resStandings ? (
        <VisualDiagram
          canShowStandings={bracket?.canShowStandings}
          standings={resStandings?.result.hits || []}
          currentMatches={currentMatches}
          eventFormat={eventFormat}
          drawImpacted={bracket?.drawImpacted}
        />
      ) : (
        <Loading />
      )}
    </>
  )
}
